import { useCallback } from 'react'
import CourseMenu from '~/components/course/CourseMenu'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import PriceBreakdown from '~/components/plan/PriceBreakdown'
import { PlanListPriceFragment } from '~/components/plan/planListPrice.frag'
import { usePlanDetail } from '~/features/plan/hooks'
import { RestaurantInfo } from '~/features/reservationInput/sd/RestaurantInfo'
import { CancelPolicy } from '~/features/reservationInput/sd/cancelPolicy'
import { Notes } from '~/features/reservationInput/sd/notes'
import { getFragmentData } from '~/lib/graphql'
import { useIsPC } from '~/lib/ua'
import { useBack, useReserveOverlay, useReserveOverlayContext } from '~/navigation'
import { sva } from '~/styled-system/css'
import { Button } from '~/ui'
import { usePlan } from '../../store/stateMachine/state'

export function CourseDetailModal() {
  const back = useBack()
  const close = useCallback(() => {
    back()
  }, [back])
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_COURSE_MODAL')
  if (context === undefined) {
    return null
  }
  return (
    <StackableSlideUpModal willBeUnmounted={willBeUnmounted}>
      <CourseDetailBody close={close} />
    </StackableSlideUpModal>
  )
}

const styles = sva({
  slots: ['container', 'content', 'plan', 'title', 'description', 'bottom', 'details'],
  base: {
    container: {
      padding: '4',
      spaceY: '2',
      _pc: {
        padding: '6',
        spaceY: '5',
      },
    },
    content: {
      spaceY: '5',
    },
    plan: {
      spaceY: '2',
    },
    title: {
      fontSize: { base: 'lg', _pc: 'xl' },
      fontWeight: 'bold',
    },
    description: {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      _pc: {
        mt: '2',
        p: '4',
        borderRadius: 'md',
        bgColor: 'gray.100',
        fontSize: 'md',
      },
    },
    bottom: {
      pb: '10',
    },
    details: {
      borderBottom: 'medium',
    },
  },
})

function CourseDetailBody({ close }: { close: () => void }) {
  const isPC = useIsPC()
  const plan = usePlan()

  const { restaurantId, planId, visitorsCount, visitDate, visitTime } =
    useReserveOverlayContext('NEW_RESERVE_INPUT') ?? {}
  const { plan: pricePlan } = usePlanDetail({
    planId,
    restaurantId,
    visitorsCount,
    visitDate,
    visitTime,
    disabled: isPC,
  })

  if (plan === undefined) {
    return null
  }

  // 元値と売値が同額の場合があるため、元値 > 売値の場合だけ元値を表示したい
  const showListPrice =
    !isPC &&
    pricePlan.listPrice != null &&
    pricePlan.bestPrice != undefined &&
    pricePlan.listPrice > pricePlan.bestPrice
  const listPriceComponents = getFragmentData(PlanListPriceFragment, pricePlan)

  const classes = styles()
  return (
    <>
      <div className={classes.container}>
        <div className={classes.content}>
          <CourseMenu
            images={plan.course.images}
            imageHeight={isPC ? 220 : 155}
            imageWidth={isPC ? 620 : 350}
            menuText={plan.course.menuText}
            showReadMoreAccordion={false}
          />
          <div className={classes.plan}>
            <h2 className={classes.title}>プラン紹介</h2>
            <p className={classes.description}>{plan.description}</p>
          </div>
          {showListPrice && (
            <div>
              <PriceBreakdown listPriceComponents={listPriceComponents} />
            </div>
          )}
          {/* コースからの予約を有効にするのは SD のみ */}
          {isPC && (
            <Button color="tertiary" size="md" spread={true} onClick={close}>
              閉じる
            </Button>
          )}
        </div>
      </div>
      {!isPC && (
        <div className={classes.bottom}>
          <div className={classes.details}>
            <CancelPolicy size="lg" border />
            <Notes size="lg" border />
            <RestaurantInfo size="lg" border />
          </div>
        </div>
      )}
    </>
  )
}
